import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  ARRAY_CONDITIONS,
  FilterForm,
  FilterTypeItem,
  FilterTypeValue,
  NUMBER_CONDITIONS,
} from 'src/app/interfaces/groups-filters/groups-filters.interface';
import { GroupMemberInterface } from 'src/app/interfaces/groups/group.interface';
import { SpinnerService } from 'src/app/services/spinner/spinner.service';
import { ProductsSubprojectService } from 'src/app/services/products-subproject/products-subproject.service';
import { UsersService } from 'src/app/services/users/users.service';

@Component({
  selector: 'app-group-filter',
  templateUrl: './group-filter.component.html',
  styleUrls: ['./group-filter.component.scss'],
})
export class GroupFilterComponent implements OnInit {
  @Input() form!: FormGroup<FilterForm>;
  @Input() groupInterface?: GroupMemberInterface;
  public readonly valueSearchControl = new FormControl('', { nonNullable: true });
  public readonly selectSearchThreshold = 8;

  public types: FilterTypeItem[] = [
    {
      value: 'product',
      label: 'Product',
    },
    {
      value: 'city',
      label: 'City',
    },
    {
      value: 'state',
      label: 'State',
    },
    {
      value: 'age',
      label: 'Age',
    },
  ];
  public filterType: FilterTypeValue | null = null;
  private arrayConditions = [
    {
      value: ARRAY_CONDITIONS.CONTAINS_ONE_OF,
      label: 'Contains one of',
    },
    {
      value: ARRAY_CONDITIONS.CONTAINS_ALL_OF,
      label: 'Contains all of',
    },
    {
      value: ARRAY_CONDITIONS.NOT_CONTAINS_ONE_OF,
      label: 'Not contains one of',
    },
    {
      value: ARRAY_CONDITIONS.NOT_CONTAINS_ALL_OF,
      label: 'Not contains all of',
    },
  ];
  private numberConditions = [
    {
      value: NUMBER_CONDITIONS.IN,
      label: 'In',
    },
    {
      value: NUMBER_CONDITIONS.NOT_IN,
      label: 'Not in',
    },
    {
      value: NUMBER_CONDITIONS.GREATER_THAN,
      label: 'Greater than',
    },
    {
      value: NUMBER_CONDITIONS.LESS_THAN,
      label: 'Less than',
    },
    {
      value: NUMBER_CONDITIONS.GREATER_THAN_OR_EQUAL,
      label: 'Greater than or equal',
    },
    {
      value: NUMBER_CONDITIONS.LESS_THAN_OR_EQUAL,
      label: 'Less than or equal',
    },
  ];
  values: { value: string; label: string }[] = [];

  constructor(
    private spinnerService: SpinnerService,
    private stripeProductsService: ProductsSubprojectService,
    private snackBar: MatSnackBar,
    private usersService: UsersService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.form.get('type')!.valueChanges.subscribe((value: FilterTypeValue | null) => {
      this.form.get('condition')!.reset();
      this.form.get('values')!.reset();
      this.valueSearchControl.setValue('', { emitEvent: false });
      if (value) {
        this.filterType = value;
        this.getValues(value);
      }
    });

    this.form
      .get('condition')!
      .valueChanges.subscribe((_condition: ARRAY_CONDITIONS | NUMBER_CONDITIONS | null) => {
        this.form.get('values')!.reset();
      });
  }

  public getTypes(): FilterTypeItem[] {
    const availableTypes = {
      [GroupMemberInterface.ExistingUser]: this.types,
      [GroupMemberInterface.Brand]: this.types.filter((t) => t.value === 'product'),
    };
    return this.groupInterface
      ? availableTypes[this.groupInterface as keyof typeof availableTypes] || []
      : [];
  }

  public getConditions(): { value: string; label: string }[] {
    const type = this.form.get('type')!.value;
    const isAllowedArrayCondition = (c: { value: string }) =>
      c.value !== ARRAY_CONDITIONS.NOT_CONTAINS_ALL_OF &&
      c.value !== ARRAY_CONDITIONS.CONTAINS_ALL_OF;

    const filteredArrayConditions = this.arrayConditions.filter(isAllowedArrayCondition);

    const availableConditions: Record<string, { value: string; label: string }[]> = {
      age: this.numberConditions,
      product: this.arrayConditions,
      city: filteredArrayConditions,
      state: filteredArrayConditions,
    };

    return type ? availableConditions[type] || [] : [];
  }

  public acceptsMultipleValues(): boolean {
    const condition = this.form.get('condition')!.value;
    return (
      !!condition &&
      [
        ARRAY_CONDITIONS.CONTAINS_ALL_OF,
        ARRAY_CONDITIONS.NOT_CONTAINS_ALL_OF,
        ARRAY_CONDITIONS.CONTAINS_ONE_OF,
        ARRAY_CONDITIONS.NOT_CONTAINS_ONE_OF,
        NUMBER_CONDITIONS.IN,
        NUMBER_CONDITIONS.NOT_IN,
      ].includes(condition)
    );
  }

  public shouldShowValueSearch(): boolean {
    return this.values.length > this.selectSearchThreshold;
  }

  public getFilteredValues(): { value: string; label: string }[] {
    const search = this.valueSearchControl.value.trim().toLowerCase();
    if (!search) {
      return this.values;
    }

    return this.values.filter((value) =>
      value.label.toLowerCase().includes(search) || value.value.toLowerCase().includes(search)
    );
  }

  public trackByValue(_: number, value: { value: string; label: string }): string {
    return value.value;
  }

  private async getValues(type: FilterTypeValue): Promise<void> {
    this.spinnerService.show('Loading values...');
    try {
      const valueFetcher = {
        product: this.getProductValues.bind(this),
        city: this.getLocationValues.bind(this, 'city'),
        state: this.getLocationValues.bind(this, 'state'),
        age: () => {},
      };
      await valueFetcher[type]();
    } catch {
      const type = `${this.form.get('type')!.value || 'value'}s`;
      this.snackBar.open(`Error while loading ${type}. Try again in a few minutes.`, '', {
        duration: 2000,
      });
      this.values = [];
    }
    this.spinnerService.hide();
  }

  private async getProductValues(): Promise<void> {
    const res = await this.stripeProductsService.getActiveProducts();
    if (res.success === false) throw new Error(res.error);
    const products = res.data;
    this.values = products
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((p) => {
        return {
          label: p.name,
          value: p.stripeProductId,
        };
      });
  }

  private async getLocationValues(type: 'city' | 'state'): Promise<void> {
    console.log('Fetching locations for type:', type);
    const res = await this.usersService.getUsersLocationValues(type);
    console.log('Received locations:', res);
    if (res.success === false) throw new Error(res.error);
    const locations = res.data;
    this.values = locations
      .sort((a, b) => a.localeCompare(b))
      .map((location) => {
        return {
          label: location,
          value: location,
        };
      });
  }
}
